import type { TaskDueFilter } from '@src/features/tasks/types';
import { AutocompleteInput } from '@src/shared/ui/inputs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type TasksFilterProps = {
  value: TaskDueFilter;
  onChange: (value: TaskDueFilter) => void;
};

export default function TasksFilter({ value, onChange }: TasksFilterProps) {
  const { t } = useTranslation();

  // Overdue leads because it is what the panel opens on; "All" sits last as
  // the way back to the unnarrowed list the panel used to show.
  const options = useMemo(
    () => [
      { label: t('tasks.filter_overdue', 'Overdue'), value: 'overdue' as const },
      {
        label: t('tasks.filter_upcoming', 'Upcoming'),
        value: 'upcoming' as const,
      },
      { label: t('common.all', 'All'), value: 'all' as const },
    ],
    [t],
  );

  return (
    <AutocompleteInput
      aria-label={t('tasks.filter_label', 'Task filter')}
      value={value}
      options={options}
      // Nothing reaches this with null while the clear button is hidden, but
      // the keyboard can still empty the input; "All" is the option that means
      // what an empty filter would.
      onChange={(nextValue) => onChange(nextValue ?? 'all')}
      placeholder={t('common.all', 'All')}
      sx={{
        '& .MuiTextField-root': {
          margin: 0,
        },
        // One of the three is always chosen, so there is no cleared state to
        // offer. `AutocompleteInput` pins MUI's DisableClearable to false, so
        // the button has to go from the style rather than the prop.
        '& .MuiAutocomplete-clearIndicator': {
          display: 'none',
        },
      }}
    />
  );
}
